import React from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Star } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { getPriceInfo, discountPercent } from "@/lib/pricing";
import { resolveImageUri, formatINR } from "@/lib/api";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addItem, verified } = useStore();
  const info = getPriceInfo(product, verified);
  const disc = discountPercent(info);

  const add = (e) => {
    e.stopPropagation();
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/product/${product.id}`)}
      className="group cursor-pointer flex flex-col"
    >
      <div className="relative w-full aspect-[4/5] bg-mist overflow-hidden rounded-lg">
        <img
          src={resolveImageUri(product.image)}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <button
          data-testid={`quick-add-${product.id}`}
          onClick={add}
          className="absolute bottom-2.5 right-2.5 w-9 h-9 rounded-full bg-paper shadow-md flex items-center justify-center text-ink hover:bg-brand hover:text-white transition-colors"
          aria-label="add to cart"
        >
          <Plus size={18} />
        </button>
        {disc > 0 && (
          <span className="absolute top-2.5 left-2.5 bg-crimson text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-sm">{disc}% OFF</span>
        )}
        {product.buy3get1 && (
          <span className="absolute top-2.5 right-2.5 bg-brand text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm" data-testid={`combo-badge-${product.id}`}>BUY 3 GET 1</span>
        )}
      </div>
      <p className="mt-2.5 text-[11px] uppercase tracking-wider text-muted2 font-medium">{product.weight || product.category}</p>
      <h3 className="font-display text-xl leading-tight text-ink mt-0.5 line-clamp-2">{product.name}</h3>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-sm font-semibold text-ink">{formatINR(info.unit)}</span>
        {info.compareAt !== null && (
          <span className="text-xs text-muted2 line-through">{formatINR(info.compareAt)}</span>
        )}
      </div>
      {(product.rating_count || 0) > 0 && (
        <div className="flex items-center gap-1 mt-0.5 text-[11px] text-muted2">
          <Star size={11} className="fill-warn text-warn" />
          <span className="font-semibold text-ink">{product.rating_avg}</span>
          <span>({product.rating_count})</span>
        </div>
      )}
      {verified && Number(product.bv) > 0 && (
        <p className="text-[11px] font-semibold text-brand mt-0.5">BV {Number(product.bv)}</p>
      )}
    </div>
  );
}
